/**
 * Trovva — NJAR Document Template Manager
 *
 * Broker/admin uploads licensed NJAR form templates once here.
 * Templates become available as "Generate" buttons on every deal's
 * Docs tab checklist. Each template maps to a checklist item
 * (PSA, Disclosure, Attorney Letter, etc.) and auto-fills with
 * deal data when instantiated.
 *
 * Only broker/admin roles can access this section.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  FileText, Plus, Trash2, Upload, Check, Loader2, AlertCircle,
  ChevronDown, ChevronRight, Shield, Eye,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { theme } from '../../lib/theme';

// Checklist items that templates can map to
const CHECKLIST_ITEMS = [
  { key: 'contract', label: 'Purchase & Sale Agreement' },
  { key: 'disclosure', label: 'Seller Disclosure Statement' },
  { key: 'lead_paint', label: 'Lead-Based Paint Disclosure' },
  { key: 'attorney_letter', label: 'Attorney Review Letter' },
  { key: 'agency_disclosure', label: 'Agency Disclosure Form' },
  { key: 'wire_fraud', label: 'Wire Fraud Warning' },
  { key: 'home_inspection', label: 'Home Inspection Report' },
  { key: 'title_commitment', label: 'Title Commitment' },
  { key: 'mortgage_commitment', label: 'Mortgage Commitment Letter' },
  { key: 'homeowner_insurance', label: "Homeowner's Insurance Binder" },
  { key: 'closing_disclosure', label: 'Closing Disclosure (CD)' },
  { key: 'deed', label: 'Deed' },
  { key: 'listing_agreement', label: 'Listing Agreement' },
  { key: 'lease', label: 'Residential Lease' },
  { key: 'flood_risk', label: 'Flood Risk Disclosure' },
  { key: 'opra_notice', label: 'OPRA Notice' },
  { key: 'other', label: 'Other / Custom' },
];

// ── Auto-categorization rules for NJAR filenames ──
const NJAR_CATEGORIZATION = [
  // Contract & addenda
  { pattern: /Contract_of_Sale|Vacant_Land.*Contract/i, kind: 'contract', clean: m => m.replace(/_ts\d+$/, '').replace(/_/g, ' ').replace(/ - \d+$/, '').trim() },
  { pattern: /Timeline_for_Contract/i, kind: 'contract', clean: m => 'Timeline for Contract of Sale' },
  { pattern: /Addenda_Buyers_Property_Sale/i, kind: 'contract', clean: () => 'Addendum — Buyer Property Sale Contingency' },
  { pattern: /Addenda_Condominium/i, kind: 'contract', clean: () => 'Addendum — Condo / HOA' },
  { pattern: /Addenda_FHAVA/i, kind: 'contract', clean: () => 'Addendum — FHA/VA Loans' },
  { pattern: /Addenda_New_Construction/i, kind: 'contract', clean: () => 'Addendum — New Construction' },
  { pattern: /Addenda_Private_Sewage/i, kind: 'contract', clean: () => 'Addendum — Private Sewage Disposal' },
  { pattern: /Addenda_Private_Well/i, kind: 'contract', clean: () => 'Addendum — Private Well Testing' },
  { pattern: /Addenda_Properties_With_Three/i, kind: 'contract', clean: () => 'Addendum — Properties With 3+ Units' },
  { pattern: /Addenda_Underground_Fuel/i, kind: 'contract', clean: () => 'Addendum — Underground Fuel Tanks' },
  { pattern: /Addendum_Regarding_Solar/i, kind: 'contract', clean: () => 'Addendum — Solar Panel Systems' },
  { pattern: /Addendum_Regarding_Swimming/i, kind: 'contract', clean: () => 'Addendum — Swimming Pools' },
  // Disclosures
  { pattern: /Seller_Property_Condition_Disclosure/i, kind: 'disclosure', clean: () => 'Seller Property Condition Disclosure Statement' },
  { pattern: /Lead_Paint_Disclosure.*Contract|Lead.*Paint.*Contract/i, kind: 'lead_paint', clean: () => 'Lead-Based Paint Disclosure (Contract)' },
  { pattern: /Lead.*Paint.*Lease|Addendum.*Lead.*Paint.*Lease/i, kind: 'lead_paint', clean: () => 'Lead-Based Paint Disclosure (Lease)' },
  // Agency forms
  { pattern: /Consumer_Information_Statement/i, kind: 'agency_disclosure', clean: () => 'Consumer Information Statement' },
  { pattern: /Exclusive_Buyer_Agency/i, kind: 'agency_disclosure', clean: () => 'Exclusive Buyer Agency Agreement' },
  { pattern: /Exclusive_Tenant_Agency/i, kind: 'agency_disclosure', clean: () => 'Exclusive Tenant Agency Agreement' },
  { pattern: /Informed_Consent.*Dual.*Buyer/i, kind: 'agency_disclosure', clean: () => 'Dual Agency Consent — Buyer' },
  { pattern: /Informed_Consent.*Dual.*Seller/i, kind: 'agency_disclosure', clean: () => 'Dual Agency Consent — Seller' },
  { pattern: /Informed_Consent.*Dual.*Landlord/i, kind: 'agency_disclosure', clean: () => 'Dual Agency Consent — Landlord' },
  { pattern: /Informed_Consent.*Dual.*Tenant/i, kind: 'agency_disclosure', clean: () => 'Dual Agency Consent — Tenant' },
  { pattern: /Designated_Agency.*Buyer/i, kind: 'agency_disclosure', clean: () => 'Designated Agency Consent — Buyer' },
  { pattern: /Designated_Agency.*Seller/i, kind: 'agency_disclosure', clean: () => 'Designated Agency Consent — Seller' },
  { pattern: /Designated_Agency.*Landlord/i, kind: 'agency_disclosure', clean: () => 'Designated Agency Consent — Landlord' },
  { pattern: /Designated_Agency.*Tenant/i, kind: 'agency_disclosure', clean: () => 'Designated Agency Consent — Tenant' },
  // Wire fraud
  { pattern: /Wire_Fraud/i, kind: 'wire_fraud', clean: () => 'Wire Fraud Notice' },
  // Flood risk
  { pattern: /Flood_Risk/i, kind: 'flood_risk', clean: () => 'Flood Risk Disclosure' },
  // OPRA notice
  { pattern: /Open_Public_Records/i, kind: 'opra_notice', clean: () => 'OPRA Notice to Buyers and Sellers' },
  // Lease forms
  { pattern: /Residential_Lease(?!.*Listing)/i, kind: 'lease', clean: () => 'Residential Lease' },
  { pattern: /Lease_Addendum_Permitting_Pets/i, kind: 'lease', clean: () => 'Lease Addendum — Permitting Pets' },
  { pattern: /Information_Release.*Lease/i, kind: 'lease', clean: () => 'Information Release for Residential Lease' },
  // Listing agreement
  { pattern: /Listing_Agreement|Residential_Listing/i, kind: 'listing_agreement', clean: () => 'Residential Listing Agreement' },
];

function categorizeFile(filename) {
  for (const rule of NJAR_CATEGORIZATION) {
    if (rule.pattern.test(filename)) {
      return { kind: rule.kind, name: rule.clean(filename) };
    }
  }
  // Fallback: clean filename as name, use 'other'
  const cleaned = filename.replace(/\.pdf$/i, '').replace(/_ts\d+$/, '').replace(/_/g, ' ').replace(/ - \d+$/, '').trim();
  return { kind: 'other', name: cleaned };
}

export default function TemplateManager({ showToast }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded] = useState(true);

  // Upload form state
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [checklistKey, setChecklistKey] = useState('');
  const [description, setDescription] = useState('');

  // Bulk upload state
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkPreviews, setBulkPreviews] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/deal-templates', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await r.json();
      if (r.ok) setTemplates(data.templates || []);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async () => {
    if (!file || !name.trim()) {
      showToast?.('Please select a PDF and enter a name');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showToast?.('File too large — 10 MB max');
      return;
    }
    setUploading(true);
    try {
      const base64 = await fileToBase64(file);
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/deal-templates', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          kind: checklistKey || 'other',
          description: description.trim() || null,
          jurisdiction: 'NJ',
          base64,
          mime: 'application/pdf',
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Upload failed');
      showToast?.(`Template "${name.trim()}" uploaded`);
      setFile(null); setName(''); setChecklistKey(''); setDescription(''); setShowUpload(false);
      load();
    } catch (e) { showToast?.(e.message); }
    finally { setUploading(false); }
  };

  const handleDelete = async (id, tplName) => {
    if (!confirm(`Remove template "${tplName}"? Existing generated documents won't be affected.`)) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`/api/deal-templates?id=${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!r.ok) { const j = await r.json(); throw new Error(j.error); }
      showToast?.('Template removed');
      load();
    } catch (e) { showToast?.(e.message); }
  };

  // ── Bulk upload handlers ──
  const handleBulkSelect = (e) => {
    const files = Array.from(e.target.files || []).filter(f => f.type === 'application/pdf');
    if (files.length === 0) return;
    setBulkFiles(files);
    const previews = files.map(f => {
      const cat = categorizeFile(f.name);
      const item = CHECKLIST_ITEMS.find(i => i.key === cat.kind);
      return { file: f, name: cat.name, kind: cat.kind, kindLabel: item?.label || 'Other / Custom', size: f.size };
    });
    setBulkPreviews(previews);
  };

  const updateBulkPreview = (idx, field, value) => {
    setBulkPreviews(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      if (field === 'kind') {
        const item = CHECKLIST_ITEMS.find(i => i.key === value);
        next[idx].kindLabel = item?.label || 'Other / Custom';
      }
      return next;
    });
  };

  const handleBulkUpload = async () => {
    if (bulkPreviews.length === 0) return;
    setBulkUploading(true);
    setBulkProgress({ done: 0, total: bulkPreviews.length });
    let succeeded = 0;
    let failed = 0;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { showToast?.('Session expired'); return; }
      const token = session.access_token;

      // Upload one at a time to avoid Vercel body size limits
      for (let i = 0; i < bulkPreviews.length; i++) {
        const p = bulkPreviews[i];
        try {
          const base64 = await fileToBase64(p.file);
          const r = await fetch('/api/deal-templates', {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: p.name, kind: p.kind, base64, jurisdiction: 'NJ', mime: 'application/pdf' }),
          });
          if (r.ok) { succeeded++; } else { failed++; }
        } catch { failed++; }
        setBulkProgress({ done: i + 1, total: bulkPreviews.length });
      }

      showToast?.(
        `Uploaded ${succeeded} of ${bulkPreviews.length} templates${failed > 0 ? ` (${failed} failed)` : ''}`,
        failed > 0 ? 'warning' : 'success'
      );
      setBulkFiles([]);
      setBulkPreviews([]);
      load();
    } catch (e) { showToast?.(e.message, 'error'); }
    finally { setBulkUploading(false); }
  };

  return (
    <div style={{
      background: theme.colors.card, borderRadius: 12,
      border: `1px solid ${theme.colors.border}`, marginBottom: 20,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <button onClick={() => setExpanded(!expanded)} style={{
        width: '100%', padding: '14px 18px', border: 'none', cursor: 'pointer',
        background: 'transparent', display: 'flex', alignItems: 'center', gap: 10,
        textAlign: 'left',
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10, flexShrink: 0,
          background: `${theme.colors.primary}10`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <FileText size={18} color={theme.colors.primary} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.colors.text }}>NJAR Document Templates</div>
          <div style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 2 }}>
            Upload your licensed NJAR forms once — they auto-fill with deal data for every transaction
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
            background: templates.length > 0 ? `${theme.colors.success}12` : `${theme.colors.warning}12`,
            color: templates.length > 0 ? theme.colors.success : '#92400E',
          }}>{templates.length} template{templates.length !== 1 ? 's' : ''}</span>
          {expanded ? <ChevronDown size={14} color={theme.colors.textLight} /> : <ChevronRight size={14} color={theme.colors.textLight} />}
        </div>
      </button>

      {expanded && (
        <div style={{ padding: '0 18px 18px', borderTop: `1px solid ${theme.colors.border}` }}>

          {/* Info banner */}
          <div style={{
            padding: '10px 12px', marginTop: 14, marginBottom: 14, borderRadius: 8,
            background: `${theme.colors.primary}05`, border: `1px solid ${theme.colors.primary}15`,
            fontSize: 11, color: theme.colors.textLight, lineHeight: 1.5,
            display: 'flex', alignItems: 'flex-start', gap: 6,
          }}>
            <Shield size={12} color={theme.colors.primary} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>
              NJAR forms are copyrighted — each brokerage uploads their own licensed copies.
              When an agent generates a document for a deal, Trovva auto-fills the template with
              deal data (buyer name, property address, price, dates, etc.) and opens it in
              the PDF Form Filler for review.
            </span>
          </div>

          {/* Upload buttons */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            <button onClick={() => { setShowUpload(!showUpload); setBulkPreviews([]); setBulkFiles([]); }} style={{
              padding: '8px 14px', borderRadius: 8, border: 'none',
              background: showUpload ? theme.colors.textLight : theme.colors.primary,
              color: 'white', fontSize: 12, fontWeight: 700, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 5,
            }}>
              <Plus size={13} /> {showUpload ? 'Cancel' : 'Upload Template'}
            </button>
            <label style={{
              padding: '8px 14px', borderRadius: 8, border: 'none',
              background: bulkPreviews.length > 0 ? theme.colors.textLight : '#16A34A',
              color: 'white', fontSize: 12, fontWeight: 700, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 5,
            }}>
              <Upload size={13} /> {bulkPreviews.length > 0 ? 'Change Selection' : 'Bulk Upload Folder'}
              <input type="file" accept="application/pdf" multiple style={{ display: 'none' }}
                onChange={e => { handleBulkSelect(e); setShowUpload(false); }} />
            </label>
          </div>

          {/* ── Bulk Upload Preview ── */}
          {bulkPreviews.length > 0 && (
            <div style={{
              padding: 16, borderRadius: 10, marginBottom: 14,
              background: '#16A34A08', border: '1px solid #16A34A20',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text }}>
                    Bulk Upload — {bulkPreviews.length} PDFs detected
                  </div>
                  <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 2 }}>
                    Auto-categorized by filename. Review and adjust categories below, then upload all at once.
                  </div>
                </div>
                <button onClick={() => { setBulkFiles([]); setBulkPreviews([]); }} style={{
                  background: 'transparent', border: 'none', cursor: 'pointer', color: theme.colors.textLight, padding: 4,
                }}>✕</button>
              </div>

              {/* Category summary chips */}
              {(() => {
                const counts = {};
                bulkPreviews.forEach(p => { counts[p.kind] = (counts[p.kind] || 0) + 1; });
                return (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                    {Object.entries(counts).map(([k, c]) => {
                      const item = CHECKLIST_ITEMS.find(i => i.key === k);
                      return (
                        <span key={k} style={{
                          fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 5,
                          background: `${theme.colors.primary}10`, color: theme.colors.primary,
                        }}>
                          {item?.label || k} ({c})
                        </span>
                      );
                    })}
                  </div>
                );
              })()}

              {/* File list */}
              <div style={{ maxHeight: 400, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {bulkPreviews.map((p, idx) => (
                  <div key={idx} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                    borderRadius: 6, background: theme.colors.card, border: `1px solid ${theme.colors.border}`,
                  }}>
                    <FileText size={14} color={theme.colors.primary} style={{ flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <input
                        value={p.name}
                        onChange={e => updateBulkPreview(idx, 'name', e.target.value)}
                        style={{
                          width: '100%', border: 'none', background: 'transparent', fontSize: 12,
                          fontWeight: 600, color: theme.colors.text, outline: 'none', padding: 0,
                        }}
                      />
                    </div>
                    <select
                      value={p.kind}
                      onChange={e => updateBulkPreview(idx, 'kind', e.target.value)}
                      style={{
                        fontSize: 10, fontWeight: 600, padding: '3px 6px', borderRadius: 4,
                        border: `1px solid ${theme.colors.border}`, color: theme.colors.primary,
                        background: theme.colors.card, cursor: 'pointer', flexShrink: 0,
                      }}
                    >
                      {CHECKLIST_ITEMS.map(item => (
                        <option key={item.key} value={item.key}>{item.label}</option>
                      ))}
                    </select>
                    <span style={{ fontSize: 10, color: theme.colors.textLight, flexShrink: 0 }}>
                      {(p.size / 1024).toFixed(0)} KB
                    </span>
                  </div>
                ))}
              </div>

              {/* Upload all button */}
              <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                <button onClick={handleBulkUpload} disabled={bulkUploading} style={{
                  padding: '10px 20px', borderRadius: 8, border: 'none',
                  background: '#16A34A', color: 'white',
                  fontSize: 13, fontWeight: 700, cursor: bulkUploading ? 'wait' : 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  opacity: bulkUploading ? 0.7 : 1,
                }}>
                  {bulkUploading ? (
                    <><Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Uploading {bulkProgress.done}/{bulkProgress.total}…</>
                  ) : (
                    <><Upload size={13} /> Upload All {bulkPreviews.length} Templates</>
                  )}
                </button>
                {bulkUploading && (
                  <div style={{ flex: 1, height: 4, borderRadius: 2, background: theme.colors.border, overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: 2, background: '#16A34A', width: `${(bulkProgress.done / bulkProgress.total) * 100}%`, transition: 'width 0.3s' }} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Single upload form */}
          {showUpload && (
            <div style={{
              padding: 16, borderRadius: 10, marginBottom: 14,
              background: `${theme.colors.primary}04`, border: `1px solid ${theme.colors.primary}15`,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text, marginBottom: 12 }}>
                Upload NJAR Form Template
              </div>

              {/* File picker */}
              <label style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px',
                borderRadius: 8, border: `2px dashed ${file ? theme.colors.success : theme.colors.border}`,
                background: file ? '#F0FDF4' : theme.colors.card, cursor: 'pointer', marginBottom: 10,
              }}>
                {file ? (
                  <>
                    <Check size={14} color={theme.colors.success} />
                    <span style={{ fontSize: 12, color: theme.colors.text, fontWeight: 500 }}>{file.name}</span>
                    <span style={{ fontSize: 10, color: theme.colors.textLight, marginLeft: 'auto' }}>
                      {(file.size / 1024).toFixed(0)} KB
                    </span>
                  </>
                ) : (
                  <>
                    <Upload size={14} color={theme.colors.textLight} />
                    <span style={{ fontSize: 12, color: theme.colors.textLight }}>Click to select PDF (max 10 MB)</span>
                  </>
                )}
                <input type="file" accept="application/pdf" style={{ display: 'none' }}
                  onChange={e => { const f = e.target.files?.[0]; if (f) setFile(f); }}
                />
              </label>

              {/* Template name */}
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Template name (e.g., NJAR Purchase & Sale Agreement)"
                style={{
                  width: '100%', padding: '8px 10px', borderRadius: 8, fontSize: 12,
                  border: `1px solid ${theme.colors.border}`, color: theme.colors.text,
                  background: theme.colors.card, boxSizing: 'border-box', marginBottom: 8,
                }}
              />

              {/* Map to checklist item */}
              <select
                value={checklistKey}
                onChange={e => {
                  setChecklistKey(e.target.value);
                  if (!name.trim() && e.target.value !== 'other') {
                    const item = CHECKLIST_ITEMS.find(i => i.key === e.target.value);
                    if (item) setName(item.label);
                  }
                }}
                style={{
                  width: '100%', padding: '8px 10px', borderRadius: 8, fontSize: 12,
                  border: `1px solid ${theme.colors.border}`, color: theme.colors.text,
                  background: theme.colors.card, boxSizing: 'border-box', marginBottom: 8,
                }}
              >
                <option value="">Maps to checklist item…</option>
                {CHECKLIST_ITEMS.map(item => (
                  <option key={item.key} value={item.key}>{item.label}</option>
                ))}
              </select>

              {/* Description */}
              <input
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Description (optional)"
                style={{
                  width: '100%', padding: '8px 10px', borderRadius: 8, fontSize: 12,
                  border: `1px solid ${theme.colors.border}`, color: theme.colors.text,
                  background: theme.colors.card, boxSizing: 'border-box', marginBottom: 12,
                }}
              />

              <button onClick={handleUpload} disabled={uploading || !file || !name.trim()} style={{
                padding: '8px 16px', borderRadius: 8, border: 'none',
                background: theme.colors.primary, color: 'white',
                fontSize: 12, fontWeight: 700, cursor: uploading ? 'wait' : 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 5,
                opacity: (uploading || !file || !name.trim()) ? 0.5 : 1,
              }}>
                {uploading ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Upload size={12} />}
                {uploading ? 'Uploading…' : 'Upload Template'}
              </button>
            </div>
          )}

          {/* Template list */}
          {loading ? (
            <div style={{ padding: 20, textAlign: 'center', color: theme.colors.textLight, fontSize: 12 }}>
              <Loader2 size={14} style={{ animation: 'spin 1s linear infinite', marginRight: 6 }} /> Loading templates…
            </div>
          ) : templates.length === 0 ? (
            <div style={{
              padding: '24px 16px', textAlign: 'center', borderRadius: 10,
              background: '#FEF3C7', border: '1px solid #F59E0B30',
            }}>
              <AlertCircle size={20} color="#92400E" style={{ marginBottom: 6 }} />
              <div style={{ fontSize: 13, fontWeight: 600, color: '#92400E' }}>No templates uploaded yet</div>
              <div style={{ fontSize: 11, color: '#92400E', marginTop: 4, lineHeight: 1.5, maxWidth: 320, margin: '4px auto 0' }}>
                Upload your licensed NJAR forms above. Once uploaded, agents will see "Generate"
                buttons on every deal's document checklist.
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {templates.map(tpl => {
                const checklistItem = CHECKLIST_ITEMS.find(i => i.key === tpl.kind);
                return (
                  <div key={tpl.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                    borderRadius: 8, border: `1px solid ${theme.colors.border}`,
                    background: theme.colors.card,
                  }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                      background: `${theme.colors.primary}08`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <FileText size={15} color={theme.colors.primary} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text }}>
                        {tpl.name}
                      </div>
                      <div style={{ fontSize: 10, color: theme.colors.textLight, marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {checklistItem && checklistItem.key !== 'other' && (
                          <span style={{
                            fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4,
                            background: `${theme.colors.primary}10`, color: theme.colors.primary,
                            textTransform: 'uppercase',
                          }}>{checklistItem.label}</span>
                        )}
                        <span>{tpl.jurisdiction || 'NJ'}</span>
                        {tpl.created_at && <span>Uploaded {new Date(tpl.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
                      </div>
                      {tpl.description && (
                        <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 3 }}>{tpl.description}</div>
                      )}
                    </div>
                    <button onClick={() => handleDelete(tpl.id, tpl.name)} title="Remove template" style={{
                      width: 28, height: 28, borderRadius: 6, border: `1px solid ${theme.colors.border}`,
                      background: 'transparent', cursor: 'pointer', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', color: theme.colors.textLight,
                      flexShrink: 0,
                    }}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <style>{`@keyframes spin { from{transform:rotate(0)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const result = r.result;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}
